import React, { useEffect, useRef, useState } from "react";
import { Gift, Lock, Copy, Check } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import AuthDialog from "../components/AuthDialog";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { wagerTiers, formatUSD, brand } from "../mock";
import { RankBadge } from "../components/RankBadge";
import { RewardClaimDialog } from "../components/RewardClaimDialog";
import { useAuth } from "../auth";

// Existing per-rank reward highlights; badge artwork uses the original rank colors.
const rankColor = {
  Bronze: "#cd7f32",
  Silver: "#c7ccd1",
  Gold: "#f5c518",
  "Platinum 1": "#7fd1c4",
  "Platinum 2": "#5fc9d6",
  "Platinum 3": "#4bb8e8",
  "Platinum 4": "#5a9df0",
  "Platinum 5": "#7b86f2",
  "Platinum 6": "#9d7bf2",
  Diamond: "#4cc9f0",
};

const Rewards = () => {
  const { toast } = useToast();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [copied, setCopied] = useState(false);
  const { user, loading } = useAuth();
  const [claimTier, setClaimTier] = useState(null);
  const claimTrigger = useRef(null);
  const canClaim = !loading && Boolean(user);

  useEffect(() => {
    if (!canClaim) setClaimTier(null);
  }, [canClaim]);

  const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); };

  const copyCode = () => {
    navigator.clipboard?.writeText(brand.affiliateCode);
    setCopied(true);
    toast({ title: "Code copied", description: `${brand.affiliateCode} copied to clipboard.` });
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative">
      <Sidebar onAuth={openAuth} />

      <div className="content-shift pt-16">
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          {/* Hero banner */}
          <div className="relative overflow-hidden rounded-3xl glass p-8 sm:p-10 mb-6">
            <div className="relative flex flex-wrap items-center justify-between gap-6">
              <div className="min-w-0 flex-1 basis-80">
                <h1 className="rewards-page-title text-4xl sm:text-5xl text-white" data-testid="wager-rewards-title">
                  Wager <span className="text-emerald-300">Rewards</span>
                </h1>
                <p className="mt-3 text-slate-400 max-w-lg">
                  Climb the VIP ranks on Stake and claim a bonus at every tier — from
                  Bronze all the way to Diamond.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 min-w-0">
                <button
                  onClick={copyCode}
                  data-testid="wager-copy-code"
                  className="flex items-center gap-2 rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
                >
                  Code: <span className="font-bold text-emerald-300">{brand.affiliateCode}</span>
                  {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-slate-400" />}
                </button>
                <Button
                  asChild
                  className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-11 px-5"
                >
                  <a href="https://stake.com/?c=GreenVenom" data-testid="wager-signup-link" target="_blank" rel="noopener noreferrer">
                    Sign Up
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Table header (desktop) */}
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-widest text-slate-500">
            <div className="col-span-4">Rank</div>
            <div className="col-span-3">Requirement</div>
            <div className="col-span-2">Reward</div>
            <div className="col-span-3 text-right">Status</div>
          </div>

          {/* Tiers */}
          <div className="space-y-3">
            {wagerTiers.map((t) => (
              <div
                key={t.rank}
                data-testid={`wager-tier-${t.rank.toLowerCase().replace(/\s+/g, "-")}`}
                className="rank-row grid grid-cols-2 md:grid-cols-12 items-center gap-3 rounded-2xl glass px-5 py-4 transition-all"
                style={{ "--rank": rankColor[t.rank] }}
              >
                <div className="col-span-2 md:col-span-4 flex items-center gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-black/20 ring-1 ring-white/10">
                    <RankBadge rank={t.rank} />
                  </span>
                  <span className="font-display font-bold text-white uppercase tracking-wide text-sm">
                    {t.rank}
                  </span>
                </div>
                <div className="md:col-span-3">
                  <span className="md:hidden text-[10px] uppercase tracking-widest text-slate-500 block">Requirement</span>
                  <span data-testid={`wager-requirement-${t.rank.toLowerCase().replace(/\s+/g, "-")}`} className="text-slate-300 tabular-nums">{formatUSD(t.requirement)}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="md:hidden text-[10px] uppercase tracking-widest text-slate-500 block">Reward</span>
                  <span data-testid={`wager-reward-${t.rank.toLowerCase().replace(/\s+/g, "-")}`} className="font-display font-bold tabular-nums" style={{ color: rankColor[t.rank] }}>
                    {formatUSD(t.reward)}
                  </span>
                </div>
                <div className="col-span-2 md:col-span-3 flex md:justify-end">
                  <Button
                    type="button"
                    disabled={!canClaim}
                    onClick={(event) => {
                      if (!canClaim) return;
                      claimTrigger.current = event.currentTarget;
                      setClaimTier(t);
                    }}
                    data-testid={`wager-claim-${t.rank.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={canClaim ? `Claim ${t.rank} reward` : `${t.rank} reward locked — log in required`}
                    title={canClaim ? "Claim via Discord" : loading ? "Checking your session" : "Log in to unlock claiming"}
                    className={`gap-2 rounded-xl h-9 w-full md:w-28 justify-center transition-colors disabled:opacity-60 ${canClaim ? "bg-emerald-500 text-emerald-950 hover:bg-emerald-400" : "bg-white/5 ring-1 ring-white/10 text-slate-400"}`}
                  >
                    {canClaim ? <Gift className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                    {canClaim ? "Claim" : "Locked"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </main>

        <Footer />
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} mode={authMode} setMode={setAuthMode} />
      <RewardClaimDialog tier={claimTier} open={canClaim && Boolean(claimTier)} onOpenChange={(open) => { if (!open) setClaimTier(null); }} triggerRef={claimTrigger} />
    </div>
  );
};

export default Rewards;
